import { BudgetLimits } from '@/dashboards/CampaignPlanner/hooks/useBudgetLimits';
import { BUDGET_SLIDER_GAP_FACTOR } from '@/config/constants';
import { OptimisationBase } from '@/optimizer';
import { LoadingSpinner, RangeSlider, TextInput, Typography } from '@analytical-alley/ui';
import { useEffect, useRef, useState } from 'react';
import { formatCampaignTargetValue } from '@/dashboards/CampaignPlanner/helpers/formatCampaignTargetValue';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';

interface BudgetStepProps {
    budget: number;
    optimizationBase: OptimisationBase;
    budgetLimits: BudgetLimits;
    loading?: boolean;
    locale: string;
    currency: string;
    onChange: (value: number) => void;
}

export const BudgetStep = ({
    budget,
    optimizationBase,
    budgetLimits,
    loading = false,
    locale,
    currency,
    onChange,
}: BudgetStepProps) => {
    const label =
        optimizationBase === 'budget'
            ? 'Estimated media budget'
            : optimizationBase === 'media_kpi'
              ? 'Target media KPI'
              : 'Target total KPI';

    const { contributionFormatter, project: { contributionFormatting } } =
        useDashboardGlobalContext();

    const gapFactor = optimizationBase === 'budget' ? BUDGET_SLIDER_GAP_FACTOR : 0;
    const gappedMin = Math.round(budgetLimits.min * (1 + gapFactor));
    const gappedMax = Math.round(budgetLimits.max * (1 - gapFactor));
    const gapsValid = gappedMin <= gappedMax;
    const min = gapsValid ? gappedMin : Math.round((budgetLimits.min + budgetLimits.max) / 2);
    const max = gapsValid ? gappedMax : min;
    const hasRange = max > min;
    const isDisabled = loading;
    const progress = hasRange
        ? Math.min(100, Math.max(0, ((budget - min) / (max - min)) * 100))
        : 0;
    const trackBackground = `linear-gradient(to right, #3BE98B 0%, #3BE98B ${progress}%, #BED4FF7F ${progress}%, #BED4FF7F 100%)`;

    const [inputValue, setInputValue] = useState<string>(String(budget));

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        setInputValue(String(budget));
    }, [budget]);

    useEffect(() => {
        if (loading) return;
        onChangeRef.current(Math.round((min + max) / 2));
    }, [min, max, loading]);

    const commitInputValue = () => {
        const parsed = Number(inputValue);
        if (!Number.isFinite(parsed)) {
            setInputValue(String(budget));
            return;
        }
        const clamped = Math.min(Math.max(parsed, min), max);
        if (clamped !== budget) {
            onChange(clamped);
        } else {
            setInputValue(String(budget));
        }
    };

    return (
        <div className="relative">
            <LoadingSpinner show={loading} />
            <Typography variant="bodyS" className="mb-1">
                {label}
            </Typography>
            <Typography variant="bodyS" className="mb-3 text-gray-500">
                Drag the slider to set your campaign value. <br />
                Based on historical performance, chosen channels, and campaign
                timing, the allowed range is <br />
                <span className="font-semibold text-accent">
                    {formatCampaignTargetValue(
                        optimizationBase,
                        min,
                        contributionFormatter,
                        currency,
                        locale,
                    )}
                </span>{' '}
                to{' '}
                <span className="font-semibold text-accent">
                    {formatCampaignTargetValue(
                        optimizationBase,
                        max,
                        contributionFormatter,
                        currency,
                        locale,
                    )}
                </span>
                .
            </Typography>
            <div className="flex items-baseline justify-between mb-2">
                <Typography variant="bodyXS" className="text-gray-500">
                    {formatCampaignTargetValue(
                        optimizationBase,
                        min,
                        contributionFormatter,
                        currency,
                        locale,
                    )}
                </Typography>
                <div className="flex items-center gap-2 mt-3">
                    <TextInput
                        type="number"
                        inputMode="numeric"
                        min={min}
                        max={max}
                        step={1}
                        value={inputValue}
                        disabled={isDisabled}
                        onChange={(e) => setInputValue(e.target.value)}
                        onBlur={commitInputValue}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                commitInputValue();
                            }
                        }}
                        className="w-40"
                    />
                    <Typography variant="bodyXS" className="text-gray-500">
                        {optimizationBase === 'budget'
                            ? currency
                            : contributionFormatting !== 'none'
                              ? contributionFormatting
                              : undefined}
                    </Typography>
                </div>
                <Typography variant="bodyXS" className="text-gray-500">
                    {formatCampaignTargetValue(
                        optimizationBase,
                        max,
                        contributionFormatter,
                        currency,
                        locale,
                    )}
                </Typography>
            </div>

            <RangeSlider
                name="budget"
                min={min}
                max={max}
                step={1}
                value={budget}
                disabled={isDisabled}
                onChange={(e) => onChange(Number(e.target.value))}
                style={{ background: trackBackground }}
            />
        </div>
    );
};
